import React, { useMemo, useState } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { Flex } from '@withjoy/joykit';
import { AccommodationsList } from '../AccommodationsList';
import { Tabs } from '@shared/components/Tabs/Tabs';
import { StayZones } from '../StayZones';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { useStayZonesAutoPopulation } from '@shared/utils/stayZones/useStayZonesAutoPopulation';

type TabType = 'hotels' | 'stayZones';

export const TabMenuContent: React.FC = () => {
  const { tq } = useTranslation('accommodationsAdmin');
  const [activeTab, setActiveTab] = useState<TabType>('hotels');

  const { status, results } = useStayZonesAutoPopulation({
    mode: 'poll-only',
    context: 'StayZonesStatus',
  });

  const tabItems = useMemo(
    () => [
      {
        label: 'hotels',
        value: tq`Hotels`,
        isActive: activeTab === 'hotels',
        onClick: () => setActiveTab('hotels'),
      },
      {
        label: 'stayZones',
        value: tq`Stay Areas`,
        isActive: activeTab === 'stayZones',
        onClick: () => setActiveTab('stayZones'),
      },
    ],
    [activeTab, tq],
  );

  const noResults = status === 'COMPLETED' && results.length === 0;
  const shouldHideStayZones = noResults || ['IDLE', 'FAILED'].includes(status);

  if (shouldHideStayZones) {
    return <AccommodationsList />;
  }

  return (
    <Flex
      flexDirection="column"
      gap={4}
      marginTop={3}
    >
      <Flex justifyContent="center">
        <Tabs
          items={tabItems}
          tabTextProps={{
            paddingX: pxToRem(10),
            whiteSpace: 'nowrap',
          }}
        />
      </Flex>

      {activeTab === 'hotels' && <AccommodationsList />}
      {activeTab === 'stayZones' && <StayZones status={status} />}
    </Flex>
  );
};

export const TabMenu: React.FC = () => {
  const [isAccommodationsStayZonesEnabled] = useIsFeatureEnabled('accommodationsStayZones');
  return isAccommodationsStayZonesEnabled ? <TabMenuContent /> : <AccommodationsList />;
};
